// Written repeat instructions affect performance without rewriting the score.
import type { ChartMelodyNote } from '../chartMelody';
import type { IRealChart, IRealMeasure } from '../ireal/types';
import { compareF, frac, type Fraction } from './duration';
import { voiceTiming } from './timing';
import type { Part, PartMeasure, PartMeasurePlaybackRule, Score, VoiceItem } from './types';

type SourcePlaybackNote = ChartMelodyNote & {
  scorePlaybackStaff?: number;
  scorePlaybackOffset?: Fraction;
};

function playbackSources(measure: PartMeasure) {
  return new Map(measure.voices.flatMap((voice) =>
    voiceTiming(voice).map(({ event, start }) => [event.id, {
      staff: event.staff ?? voice.staff ?? 0,
      offset: start,
    }] as const),
  ));
}

/** Ephemeral provenance attached BEFORE form expansion. Cycle copies mint
 * fresh note IDs, but keep the written staff and onset, like playbackExpression. No
 * timing/pitch matching is needed, so unisons and adjusted timings stay safe. */
export function withPartPlaybackSources(part: Part | undefined, chart: IRealChart): IRealChart {
  if (!part?.measures.some((measure) => measure.playback?.length)) return chart;
  return { ...chart, measures: chart.measures.map((measure, index) => {
    const source = part.measures[index];
    if (!source?.playback?.length || !measure.melody) return measure;
    const sources = playbackSources(source);
    return { ...measure, melody: measure.melody.map((note): SourcePlaybackNote => {
      const event = sources.get(note.id);
      return event === undefined ? note : {
        ...note, scorePlaybackStaff: event.staff, scorePlaybackOffset: event.offset,
      };
    }) };
  }) };
}

function isNonnegativeFraction(value: unknown): value is Fraction {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const fraction = value as Record<string, unknown>;
  return typeof fraction.num === 'number' && Number.isSafeInteger(fraction.num) && fraction.num >= 0 &&
    typeof fraction.den === 'number' && Number.isSafeInteger(fraction.den) && fraction.den > 0;
}

export function isPartMeasurePlaybackRule(value: unknown): value is PartMeasurePlaybackRule {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const rule = value as Record<string, unknown>;
  const { startOffset, endOffset } = rule;
  if (startOffset !== undefined && !isNonnegativeFraction(startOffset)) return false;
  if (endOffset !== undefined && (
    !isNonnegativeFraction(endOffset) ||
    compareF(startOffset ?? frac(0), endOffset) >= 0
  )) return false;
  return (
    (rule.visits === undefined ||
      (Array.isArray(rule.visits) && rule.visits.length > 0 &&
        rule.visits.every((visit) => Number.isSafeInteger(visit) && visit > 0))) &&
    (rule.staff === undefined ||
      (typeof rule.staff === 'number' && Number.isSafeInteger(rule.staff) && rule.staff >= 0)) &&
    (rule.octaveShift === undefined ||
      (typeof rule.octaveShift === 'number' && Number.isSafeInteger(rule.octaveShift))) &&
    (rule.silent === undefined || typeof rule.silent === 'boolean') &&
    (rule.octaveShift !== undefined || rule.silent !== undefined)
  );
}

export function resolvePartMeasurePlayback(
  measure: PartMeasure, visit: number, staff: number, offset: Fraction = frac(0),
) {
  let octaveShift = 0;
  let silent = false;
  for (const rule of measure.playback ?? []) {
    if (rule.visits && !rule.visits.includes(visit)) continue;
    if (rule.staff !== undefined && rule.staff !== staff) continue;
    if (rule.startOffset && compareF(offset, rule.startOffset) < 0) continue;
    if (rule.endOffset && compareF(offset, rule.endOffset) >= 0) continue;
    octaveShift = rule.octaveShift ?? octaveShift;
    silent = rule.silent ?? silent;
  }
  return { octaveShift, silent };
}

/** A sounding projection for straight-through written-score audition. Event
 * IDs, exact rhythms and expressions survive; the saved/engraved score does not
 * change. Every bar uses the selected visit (normally the first). */
export function scoreForPlaybackVisit(score: Score, visit = 1): Score {
  return {
    ...score,
    parts: score.parts.map((part) => ({
      ...part,
      measures: part.measures.map((measure) => {
        if (!measure.playback?.length) return measure;
        const sources = playbackSources(measure);
        const itemsFor = (items: VoiceItem[]): VoiceItem[] => items.map((item) => {
          if (item.kind === 'tuplet') return { ...item, items: itemsFor(item.items) };
          const source = sources.get(item.id)!;
          const { octaveShift, silent } = resolvePartMeasurePlayback(measure, visit, source.staff, source.offset);
          if (silent) return { ...item, kind: 'rest', notes: [] };
          if (!octaveShift) return item;
          return { ...item, notes: item.notes.map((head) => ({
            ...head, pitch: { ...head.pitch, octave: head.pitch.octave + octaveShift },
          })) };
        });
        return { ...measure, voices: measure.voices.map((voice) => ({
          ...voice, items: itemsFor(voice.items),
        })) };
      }),
    })),
  };
}

/** Apply rules before the existing melody tie/expression merger. Its input is
 * already in performance order; call withPartPlaybackSources before any
 * expansion that replaces note IDs. The caller's written-index map also covers
 * form cycles whose copied measures have new IDs. Visits belong to this
 * itinerary, not the lifetime of a transport looping it. */
export function withPartPlaybackRules(
  part: Part | undefined,
  measures: readonly IRealMeasure[],
  writtenIndexes: readonly (number | undefined)[],
): IRealMeasure[] {
  if (!part?.measures.some((measure) => measure.playback?.length)) return [...measures];
  const visits = new Map<number, number>();
  return measures.map((measure, index) => {
    const writtenIndex = writtenIndexes[index];
    if (writtenIndex === undefined) return measure;
    const visit = (visits.get(writtenIndex) ?? 0) + 1;
    visits.set(writtenIndex, visit);
    const source = part.measures[writtenIndex];
    if (!source?.playback?.length || !measure.melody) return measure;
    const sources = playbackSources(source);
    return { ...measure, melody: measure.melody.flatMap((note) => {
      const event = sources.get(note.id);
      const staff = (note as SourcePlaybackNote).scorePlaybackStaff ?? event?.staff;
      const offset = (note as SourcePlaybackNote).scorePlaybackOffset ?? event?.offset;
      if (staff === undefined || offset === undefined) return [note];
      const { silent, octaveShift } = resolvePartMeasurePlayback(source, visit, staff, offset);
      return silent ? [] : [{ ...note, midi: note.midi + 12 * octaveShift }];
    }) };
  });
}
